export type FallbackType = 'none' | 'first' | 'prev'

export interface PaginationParam {
  pageNum: number
  pageSize: number
  [key: string]: any
}

export interface PaginationResult<T = any> {
  records: T[]
  total: number
  pageNum?: number
}

/**
 * 计算分页越界后应该跳转的目标页码
 * @param pageNum 当前页码
 * @param pageSize 每页条数
 * @param total 总条数
 * @param dataLength 当前页返回的数据条数
 * @param fallbackType 兜底策略：'none' 同步到最后一页 | 'first' 回到第一页 | 'prev' 回到上一页
 * @returns 处理后的目标页码
 */
export const applyPaginationFallback = (
  pageNum: number,
  pageSize: number,
  total: number,
  dataLength: number,
  fallbackType: FallbackType = 'prev'
): number => {
  const isOutOfRange = dataLength === 0 && total > 0 && pageNum > 1
  if (!isOutOfRange) return pageNum

  const totalPages = Math.ceil(total / pageSize) || 1

  switch (fallbackType) {
    case 'first':
      return 1
    case 'prev':
      return Math.max(1, pageNum - 1)
    case 'none':
      return Math.min(pageNum, totalPages)
    default:
      return pageNum
  }
}

/**
 * 请求分页数据并处理页码越界兜底
 * @param fetchFn 分页请求函数
 * @param param 分页参数
 * @param fallbackType 兜底策略
 * @returns 分页结果
 */
export const fetchWithPaginationFallback = async <T = any>(
  fetchFn: (param: PaginationParam) => Promise<PaginationResult<T> | any>,
  param: PaginationParam,
  fallbackType: FallbackType = 'prev'
): Promise<PaginationResult<T>> => {
  let result = await fetchFn(param)
  let records = result.records || []
  let total = result.total || 0

  const fallbackPage = applyPaginationFallback(
    param.pageNum,
    param.pageSize,
    total,
    records.length,
    fallbackType
  )

  if (fallbackPage !== param.pageNum) {
    param.pageNum = fallbackPage
    result = await fetchFn(param)
    records = result.records || []
    total = result.total || 0
  }

  return { records, total, pageNum: param.pageNum }
}
